/*
  Recently viewed products for Dawn v15

  File:
  assets/recently-viewed-products.js
*/

if (!customElements.get('recently-viewed-products')) {
  customElements.define(
    'recently-viewed-products',
    class RecentlyViewedProducts extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.sectionId = this.dataset.sectionId;
        this.storageKey = this.dataset.storageKey || 'dawn_recently_viewed_products';
        this.currentProductHandle = this.dataset.currentProductHandle || '';
        this.limit = Number.parseInt(this.dataset.limit, 10) || 4;
        this.storageLimit = Number.parseInt(this.dataset.storageLimit, 10) || 12;
        this.excludeCurrent = this.dataset.excludeCurrent === 'true';
        this.columnsMobile = Number.parseInt(this.dataset.columnsMobile, 10) || 1;
        this.swipeOnMobile = this.dataset.swipeOnMobile === 'true';

        this.list = this.querySelector('[data-recently-viewed-list]');
        this.totalElement = this.querySelector('[data-recently-viewed-total]');
        this.sliderButtons = this.querySelector('[data-recently-viewed-slider-buttons]');

        if (!this.sectionId || !this.list) return;

        this.saveCurrentProduct();
        this.renderRecentlyViewedProducts();
      }

      getStorageHandles() {
        try {
          const storedValue = window.localStorage.getItem(this.storageKey);
          const handles = JSON.parse(storedValue);

          if (!Array.isArray(handles)) return [];

          return handles
            .filter((handle) => typeof handle === 'string')
            .map((handle) => handle.trim())
            .filter(Boolean);
        } catch (error) {
          return [];
        }
      }

      setStorageHandles(handles) {
        try {
          const uniqueHandles = [...new Set(handles)]
            .filter((handle) => typeof handle === 'string')
            .map((handle) => handle.trim())
            .filter(Boolean)
            .slice(0, this.storageLimit);

          window.localStorage.setItem(this.storageKey, JSON.stringify(uniqueHandles));
        } catch (error) {
          // localStorage can be unavailable in some privacy modes.
        }
      }

      saveCurrentProduct() {
        if (!this.currentProductHandle) return;

        const handles = this.getStorageHandles();
        const nextHandles = [
          this.currentProductHandle,
          ...handles.filter((handle) => handle !== this.currentProductHandle),
        ];

        this.setStorageHandles(nextHandles);
      }

      getDisplayHandles() {
        let handles = this.getStorageHandles();

        if (this.excludeCurrent && this.currentProductHandle) {
          handles = handles.filter((handle) => handle !== this.currentProductHandle);
        }

        return handles.slice(0, this.limit);
      }

      async renderRecentlyViewedProducts() {
        const handles = this.getDisplayHandles();

        if (!handles.length) {
          this.hideSection();
          return;
        }

        const cardResults = await Promise.all(
          handles.map(async (handle) => {
            const html = await this.fetchProductCard(handle);
            return {
              handle,
              html,
            };
          })
        );

        const validCards = cardResults.filter((result) => result.html);

        if (!validCards.length) {
          this.hideSection();
          return;
        }

        this.list.innerHTML = validCards.map((result) => result.html).join('');

        this.updateSliderState(validCards.length);
        this.removeAttribute('hidden');
      }

      async fetchProductCard(handle) {
        const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';

        const productUrl = `${root}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(
          this.sectionId
        )}`;

        try {
          const response = await fetch(productUrl, {
            credentials: 'same-origin',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          });

          if (!response.ok) return '';

          const responseText = await response.text();
          const documentFragment = new DOMParser().parseFromString(responseText, 'text/html');
          const card = documentFragment.querySelector('[data-recently-viewed-card]');

          if (!card) return '';

          const productId = card.dataset.productId || this.sanitizeForId(handle);
          card.id = `Slide-${this.sectionId}-${productId}`;

          card.classList.add('recently-viewed-products__item');

          return card.outerHTML;
        } catch (error) {
          return '';
        }
      }

      updateSliderState(cardCount) {
        if (this.totalElement) {
          this.totalElement.textContent = cardCount.toString();
        }

        if (this.sliderButtons) {
          const shouldShowButtons = this.swipeOnMobile && cardCount > this.columnsMobile;
          this.sliderButtons.hidden = !shouldShowButtons;
        }

        const sliderComponent = this.querySelector('slider-component');

        if (!sliderComponent) return;

        sliderComponent.slider = sliderComponent.querySelector('[id^="Slider-"]');
        sliderComponent.sliderItems = sliderComponent.querySelectorAll('[id^="Slide-"]');
        sliderComponent.currentPageElement = sliderComponent.querySelector('.slider-counter--current');
        sliderComponent.pageTotalElement = sliderComponent.querySelector('.slider-counter--total');
        sliderComponent.prevButton = sliderComponent.querySelector('button[name="previous"]');
        sliderComponent.nextButton = sliderComponent.querySelector('button[name="next"]');

        window.requestAnimationFrame(() => {
          if (typeof sliderComponent.initPages === 'function') {
            sliderComponent.initPages();
          }

          if (typeof sliderComponent.update === 'function') {
            sliderComponent.update();
          }
        });
      }

      hideSection() {
        this.setAttribute('hidden', '');
        this.list.innerHTML = '';

        if (this.totalElement) {
          this.totalElement.textContent = '0';
        }

        if (this.sliderButtons) {
          this.sliderButtons.hidden = true;
        }
      }

      sanitizeForId(value) {
        return value.toString().replace(/[^a-zA-Z0-9_-]/g, '-');
      }
    }
  );
}